import React from 'react';
import cn from 'classnames';
import {
  Logo,
  MenuItemProps,
  DropdownButton,
  Avatar,
} from '@odin-labs/components';
import { ArrowLeftIcon, GlobeIcon } from 'components/icons';
import { useFilePreview } from 'components/filePreview';
import { AuthContext } from 'auth';
import { SelfOnboardingStepKey, SelfOnboardingStepProps } from './types';
import { Copy } from './helpers/languages';
import { languageOptions } from './utils';

export type SelfOnboardingHeaderProps = Pick<
  SelfOnboardingStepProps,
  'language' | 'onLanguageChange' | 'localize' | 'navigation' | 'state'
> & {
  canGoToPreviousStep: boolean;
};

export type LanguageMenuItem = MenuItemProps &
  Pick<SelfOnboardingHeaderProps, 'language'>;

const classes = {
  container: cn(
    'odin-sticky odin-top-0',
    // Note: Adding a high z-index to the header (e.g., 'odin-z-40')
    // causes the first item of the language selection dropdown (DropdownButton)
    // to be hidden behind the header. To avoid this UI issue,
    // the z-index should be kept lower than the dropdown's z-index (20).
    'odin-z-10',
    'odin-bg-odin-env-prod',
    'odin-flex odin-justify-between odin-items-center',
    'odin-px-7.5 odin-py-6.5',
    'print:odin-hidden',
  ),
  innerContainer:
    'odin-mx-auto odin-w-full odin-max-w-7xl odin-flex odin-justify-between',
  leftContainer: 'odin-flex odin-items-center',
  logoHolder: cn('odin-flex odin-justify-start'),
};

export function SelfOnboardingHeader(
  props: SelfOnboardingHeaderProps,
): React.ReactElement {
  const {
    language,
    onLanguageChange,
    localize,
    navigation,
    canGoToPreviousStep,
    state,
  } = props;
  const { goToPreviousStep, goToStep } = navigation;
  const { authState } = React.useContext(AuthContext);

  // Initials track the name as it becomes known (from the session user, or from
  // Basic Info as it's entered); the profile image replaces them once supplied.
  const { firstName, lastName } = state?.basicInfo ?? {};
  const userInitials = [firstName, lastName]
    .map((name) => name?.trim()?.charAt(0)?.toUpperCase())
    .filter(Boolean)
    .join('');
  const { fileUrl: profileImageUrl } = useFilePreview(
    state?.profilePhoto?.file,
  );

  // On self-onboarding, authState.signOut() clears the session and reloads the
  // page; navigating to the first step first means the reload lands on language
  // selection, and the reload purges the in-memory self-onboarding state.
  const handleSignOut = (): void => {
    goToStep(SelfOnboardingStepKey.LanguageSelection);
    authState.signOut();
  };

  const menuItems: LanguageMenuItem[] = languageOptions.map((opt) => ({
    onClick: (): void => onLanguageChange(opt),
    text: localize(Copy.language, { locale: opt.value }),
    language: opt.value,
  }));

  const languageText = menuItems.find((mi) => mi.language === language)?.text;

  return (
    <header className={classes.container}>
      <div className={classes.innerContainer}>
        <div className={classes.leftContainer}>
          {canGoToPreviousStep && (
            <button onClick={goToPreviousStep}>
              <ArrowLeftIcon className="odin-text-white odin-text-2.33xl odin-mr-5" />
            </button>
          )}
          <div className={classes.logoHolder}>
            <span className="sr-only">ODIN</span>
            <Logo logoStyle="textOnly" size="md" />
          </div>
        </div>
        <div className="odin-flex odin-items-center odin-gap-x-4">
          {(profileImageUrl || userInitials) && (
            <DropdownButton
              theme="naked"
              hideArrow
              hideFocusBorder
              anchor="bottom end"
              popupZIndex={20}
              menuItems={[
                { text: localize(Copy.sign_out), onClick: handleSignOut },
              ]}
              text={
                <Avatar
                  src={profileImageUrl}
                  placeholder={userInitials}
                  type="circular"
                  size="md"
                  objectFit="cover"
                />
              }
            />
          )}
          <DropdownButton
            menuItems={menuItems}
            icon={GlobeIcon}
            text={languageText}
            size="sm"
            theme="secondary"
            anchor="top end"
            popupZIndex={20}
            hideFocusBorder
          />
        </div>
      </div>
    </header>
  );
}
